import Ajv from 'ajv';
import CustomJsonViewer from '@src/components/CustomJsonViewer';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NewDefinitionDialog from './NewDefinitionDialog';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Skeleton,
  TextField,
  InputAdornment,
  Button,
  Snackbar,
} from '@mui/material';
import { colors } from '@src/styles';
import { TexStatus } from '@src/components';
import { useCanonicalsContext } from '../CanonicalsContext';
import { BrainApi } from '@src/services/brain/brain-api';
import { staggerChildrenSX } from '@src/styles/animations';
import {
  monoFontFamily,
  monoLabelSX,
  searchFieldSX,
  sectionCardSX,
  titleFontFamily,
  tonePillSX,
} from './canonicalStyles';

const DEFINITION_COLUMNS = 'minmax(180px, 1fr) 72px 150px 120px 116px';

export const CanonicalResourceSchema = () => {
  const {
    resourceDetails,
    extensionSubscriptionsOrgIds,
    isLoadingResource,
    organizationsMap,
    handleCanonicalOrganizationClick,
    refetchResourceDetails,
  } = useCanonicalsContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [newDefinitionVersion, setNewDefinitionVersion] = useState('0.0.1');
  const [modalViewMode, setModalViewMode] = useState<'schema' | 'raw'>('schema');
  const [jsonEditorValue, setJsonEditorValue] = useState<string>('{}');
  const [jsonErrors, setJsonErrors] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const validateConfig = useMemo(() => {
    if (!resourceDetails?.schema) return null;
    try {
      return new Ajv({ allErrors: true, verbose: true }).compile(resourceDetails.schema);
    } catch {
      return null;
    }
  }, [resourceDetails?.schema]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpenModal = () => {
    setSelectedOrganizationId('');
    setNewDefinitionVersion('0.0.1');
    setJsonEditorValue('{}');
    setJsonErrors([]);
    setModalViewMode('schema');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleJsonChange = (value: string) => {
    setJsonEditorValue(value);
    const validate = validateConfig;
    if (!validate) {
      setJsonErrors([]);
      return;
    }

    try {
      const parsedValue = JSON.parse(value);
      const valid = validate(parsedValue);

      if (!valid && validate.errors) {
        setJsonErrors(validate.errors.map((err) => `${err.instancePath} ${err.message}`));
      } else {
        setJsonErrors([]);
      }
    } catch (error) {
      if (error instanceof Error) {
        setJsonErrors([error.message]);
      }
    }
  };

  const handleCreateDefinition = async () => {
    setIsCreating(true);
    try {
      const parsed = JSON.parse(jsonEditorValue);

      if (!newDefinitionVersion) throw new Error('Sem versão definida');
      if (!selectedOrganizationId) throw new Error('Sem organização selecionada');

      const brainApiService = new BrainApi();
      await brainApiService.v1.resources.postDefinitionByResourceId({
        resourceId: resourceDetails.id,
        body: {
          config: parsed,
          organizationId: selectedOrganizationId,
          version: newDefinitionVersion,
        },
      });

      handleCloseModal();
      setSnackbar({
        open: true,
        message: `Definição para a "${organizationsMap[selectedOrganizationId]}" criada com sucesso!`,
        severity: 'success',
      });

      await refetchResourceDetails();
    } catch (e: unknown) {
      if (e instanceof Error) {
        setSnackbar({
          open: true,
          message: `Erro: ${e.message}`,
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Erro: JSON inválido',
          severity: 'error',
        });
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoadingResource) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} sx={{ color: colors.primary.yellow }} />
          <Typography variant="body2" sx={{ color: colors.primary.white, opacity: 0.8 }}>
            Carregando detalhes do resource...
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Skeleton variant="rectangular" height={60} sx={{ backgroundColor: colors.background.secondary }} />
          <Skeleton variant="rectangular" height={40} sx={{ backgroundColor: colors.background.secondary }} />
        </Box>
      </Box>
    );
  }

  if (!resourceDetails) {
    return (
      <Alert
        severity="warning"
        icon={<ErrorOutlineIcon />}
        sx={{
          backgroundColor: colors.background.secondary,
          border: `1px solid ${colors.primary.warning}`,
          color: colors.primary.white,
          '& .MuiAlert-icon': {
            color: colors.primary.warning,
          },
        }}
      >
        <Typography variant="body2">
          Não foi possível carregar os detalhes do resource. Verifique se o resourceId está correto.
        </Typography>
      </Alert>
    );
  }

  const filteredResourceDefintions = resourceDetails.definitions.filter((definition) => {
    const fantasyName = organizationsMap[definition.organizationId] || definition.organizationId;
    return (
      fantasyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      definition.organizationId.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const definitionsCount = resourceDetails.definitions?.length || 0;
  const sortedDefinitions = [...filteredResourceDefintions].sort((a, b) => {
    const fantasyNameA = organizationsMap[a.organizationId] || a.organizationId;
    const fantasyNameB = organizationsMap[b.organizationId] || b.organizationId;
    return fantasyNameA.localeCompare(fantasyNameB, 'pt-BR', { sensitivity: 'base' });
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: { md: '1 1 auto' }, minHeight: 0 }}>
      <Box
        component="dl"
        sx={{
          ...sectionCardSX,
          flexShrink: 0,
          margin: 0,
          padding: '12px 16px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '12px 32px',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="dt" sx={monoLabelSX}>
            Resource
          </Typography>
          <Typography
            component="dd"
            noWrap
            title={resourceDetails.id}
            sx={{ margin: 0, fontFamily: monoFontFamily, fontSize: '12px', color: colors.brand.textStrong }}
          >
            {resourceDetails.id}
          </Typography>
        </Box>
        <Box>
          <Typography component="dt" sx={monoLabelSX}>
            Criado em
          </Typography>
          <Typography component="dd" sx={{ margin: 0, fontSize: '12.5px', color: colors.brand.textStrong }}>
            {formatDate(resourceDetails.createdAt)}
          </Typography>
        </Box>
        <Box>
          <Typography component="dt" sx={monoLabelSX}>
            Definições
          </Typography>
          <Typography component="dd" sx={{ margin: 0, fontSize: '12.5px', color: colors.brand.textStrong }}>
            {definitionsCount} {definitionsCount === 1 ? 'organização' : 'organizações'}
          </Typography>
        </Box>
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={monoLabelSX}>Schema</Typography>
          <CustomJsonViewer data={resourceDetails.schema} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: { md: '1 1 auto' }, minHeight: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', flexShrink: 0 }}>
          <Typography
            component="h2"
            sx={{ fontFamily: titleFontFamily, fontSize: '15px', fontWeight: 600, color: colors.primary.white }}
          >
            Definições por organização
          </Typography>
          <Box sx={tonePillSX(colors.brand.textSecondary)}>{definitionsCount}</Box>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }} />
          {definitionsCount > 0 && (
            <TextField
              placeholder="Buscar organização…"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              size="small"
              inputProps={{ 'aria-label': 'Buscar organização' }}
              sx={{ ...searchFieldSX, width: { xs: '100%', sm: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.brand.textMuted, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Button
            onClick={handleOpenModal}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              height: 36,
              paddingX: 1.5,
              borderRadius: '8px',
              border: '1px solid rgba(255, 160, 21, 0.5)',
              backgroundColor: 'rgba(255, 160, 21, 0.1)',
              color: colors.primary.yellow,
              fontFamily: titleFontFamily,
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: 'rgba(255, 160, 21, 0.18)', borderColor: colors.primary.yellow },
            }}
          >
            Adicionar organização
          </Button>
        </Box>

        {definitionsCount === 0 ? (
          <Box
            sx={{
              ...sectionCardSX,
              borderStyle: 'dashed',
              padding: '24px 16px',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: '13px', color: colors.brand.textSecondary }}>
              Nenhuma organização tem definição para este recurso ainda.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ ...sectionCardSX, overflow: 'auto', flex: { md: '0 1 auto' }, minHeight: 0 }}>
            <Box sx={{ minWidth: 640 }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  display: 'grid',
                  gridTemplateColumns: DEFINITION_COLUMNS,
                  gap: 2,
                  alignItems: 'center',
                  paddingX: 2,
                  height: 34,
                  backgroundColor: colors.brand.surface,
                  borderBottom: `1px solid ${colors.brand.line}`,
                }}
              >
                <Typography sx={monoLabelSX}>Organização</Typography>
                <Typography sx={monoLabelSX}>Versão</Typography>
                <Typography sx={monoLabelSX}>Criada em</Typography>
                <Typography sx={monoLabelSX}>Extensão</Typography>
                <Typography sx={{ ...monoLabelSX, textAlign: 'right' }}>Ações</Typography>
              </Box>

              {sortedDefinitions.length > 0 ? (
                <Box sx={staggerChildrenSX('div', 12, 20)}>
                  {sortedDefinitions.map((definition) => {
                    const fantasyName = organizationsMap[definition.organizationId] || definition.organizationId;
                    const handleOpen = () => handleCanonicalOrganizationClick(definition.organizationId);
                    return (
                      <Box
                        key={definition.id}
                        role="button"
                        tabIndex={0}
                        onClick={handleOpen}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleOpen();
                          }
                        }}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: DEFINITION_COLUMNS,
                          gap: 2,
                          alignItems: 'center',
                          paddingX: 2,
                          minHeight: 44,
                          cursor: 'pointer',
                          borderBottom: `1px solid ${colors.brand.line}`,
                          '&:last-of-type': { borderBottom: 'none' },
                          transition: 'background-color 0.15s ease',
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
                          '&:hover .definition-name': { color: colors.primary.yellow },
                          '&:focus-visible': { outline: `2px solid ${colors.primary.yellow}`, outlineOffset: '-2px' },
                        }}
                      >
                        <Typography
                          className="definition-name"
                          noWrap
                          title={`${fantasyName} (${definition.organizationId})`}
                          sx={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: colors.primary.white,
                            minWidth: 0,
                            transition: 'color 0.15s ease',
                          }}
                        >
                          {fantasyName}
                        </Typography>
                        <Box sx={tonePillSX(colors.primary.yellow)}>v{definition.version}</Box>
                        <Typography
                          noWrap
                          sx={{ fontFamily: monoFontFamily, fontSize: '11px', color: colors.brand.textSecondary }}
                        >
                          {formatDate(definition.createdAt)}
                        </Typography>
                        <Box>
                          <TexStatus
                            data={{
                              status: extensionSubscriptionsOrgIds?.find((o) => o === definition.organizationId)
                                ? 'ENABLED'
                                : 'DISABLED',
                              isLoading: extensionSubscriptionsOrgIds === null,
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpen();
                            }}
                            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              height: 28,
                              paddingX: 1.25,
                              borderRadius: '8px',
                              border: `1px solid ${colors.brand.line}`,
                              color: colors.brand.textStrong,
                              fontFamily: titleFontFamily,
                              fontSize: '12px',
                              fontWeight: 500,
                              textTransform: 'none',
                              whiteSpace: 'nowrap',
                              '&:hover': { borderColor: colors.primary.yellow, color: colors.primary.white },
                            }}
                          >
                            Ver detalhes
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography sx={{ padding: '16px', fontSize: '13px', color: colors.brand.textSecondary }}>
                  Nenhuma organização corresponde a "{searchQuery}".
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <NewDefinitionDialog
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleCreateDefinition}
        version={newDefinitionVersion}
        onVersionChange={setNewDefinitionVersion}
        viewMode={modalViewMode}
        onViewModeChange={setModalViewMode}
        jsonValue={jsonEditorValue}
        onJsonChange={handleJsonChange}
        jsonErrors={jsonErrors}
        schema={resourceDetails?.schema}
        organizationId={selectedOrganizationId}
        onOrganizationIdChange={setSelectedOrganizationId}
        organizationsMap={organizationsMap}
        existingOrganizationIds={resourceDetails?.definitions.map((d) => d.organizationId) || []}
        isLoading={isCreating}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            backgroundColor:
              snackbar.severity === 'success' ? colors.primary.green : colors.primary.warning || '#f44336',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
